import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './StudyPage.css';
import EnrollmentModal from './EnrollmentModal';
import AppDownloadSection from './components/AppDownloadSection';
import { DEFAULT_APP } from './constants/mobileApps';

// Blob storage URL for study data
const BLOB_STORAGE_URL = 'https://physioactivityweb.blob.core.windows.net/studies';

const StudyPage = () => {
    const { studySlug } = useParams();
    const navigate = useNavigate();
    const [studyData, setStudyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEnrollmentModalOpen, setIsEnrollmentModalOpen] = useState(false);

    useEffect(() => {
        const fetchStudyData = async () => {
            if (!studySlug) {
                setError('Study not found');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // First, try to resolve the slug via PageIndex API
                // This supports: RowKey (code), vanityCode, or fullSlug
                let blobPath;
                try {
                    const resolveResponse = await fetch(`/api/resolve-page?type=study&slug=${encodeURIComponent(studySlug)}`);
                    if (resolveResponse.ok) {
                        const resolved = await resolveResponse.json();
                        blobPath = resolved.blobPath;
                        // Redirect to canonical URL if slug doesn't match fullSlug
                        if (resolved.fullSlug && resolved.fullSlug !== studySlug) {
                            navigate(`/research/${resolved.fullSlug}`, { replace: true });
                            return;
                        }
                    }
                } catch (resolveErr) {
                    // API not available, continue with fallback
                    console.log('resolve-page API not available, using fallback');
                }

                // Fallback: try direct blob access (original behavior)
                if (!blobPath) {
                    blobPath = `studies/${studySlug}/data.json`;
                }

                // Fetch study data from blob storage
                const dataResponse = await fetch(`https://physioactivityweb.blob.core.windows.net/${blobPath}`);

                if (!dataResponse.ok) {
                    if (dataResponse.status === 404) {
                        setError('Study not found');
                        setLoading(false);
                        return;
                    }
                    throw new Error('Failed to fetch study data');
                }

                const data = await dataResponse.json();
                setStudyData(data);

            } catch (err) {
                console.error('Error fetching study data:', err);
                setError('Failed to load study');
            } finally {
                setLoading(false);
            }
        };

        fetchStudyData();
    }, [studySlug]);

    // Helper to get initials
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    const handleOpenEnrollment = () => {
        setIsEnrollmentModalOpen(true);
    };

    const handleCloseEnrollment = () => {
        setIsEnrollmentModalOpen(false);
    };

    // Loading state
    if (loading) {
        return (
            <div className="study-page">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading study...</p>
                </div>
            </div>
        );
    }

    // Error or not found state
    if (error || !studyData) {
        return (
            <div className="study-page">
                <div className="loading-container">
                    <p>{error || 'Study not found'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="study-page">
            <div className="study-container">

                {/* Study Header Card */}
                <div className="study-card">
                    <div className="cover-photo"></div>
                    <div className="study-header">
                        <div className="study-logo">
                            {studyData.studyAcronym?.substring(0, 2) || 'St'}
                        </div>
                        {studyData.status === 'recruiting' && (
                            <div className="study-status">
                                <span className="status-dot"></span>
                                Actively Recruiting
                            </div>
                        )}
                        <h1 className="study-name">
                            <span className="study-acronym">{studyData.studyAcronym}</span> Study
                        </h1>
                        <p className="study-headline">{studyData.headline}</p>

                        {/* Sponsor Logos */}
                        <div className="sponsor-logos">
                            <span className="sponsor-text">Sponsored by</span>
                            <span className="sponsor-name">{studyData.sponsor}</span>
                        </div>
                    </div>
                </div>

                {/* About Section */}
                <div className="section">
                    <h2 className="section-title">About This Study</h2>
                    <p className="about-text">{studyData.fullDescription}</p>
                </div>

                {/* Study Overview */}
                <div className="section">
                    <h2 className="section-title">Study Overview</h2>
                    <div className="overview-grid">
                        {studyData.studyType && (
                            <div className="overview-item">
                                <span className="overview-label">Study Type</span>
                                <span className="overview-value">{studyData.studyType}</span>
                            </div>
                        )}
                        {studyData.duration && (
                            <div className="overview-item">
                                <span className="overview-label">Duration</span>
                                <span className="overview-value">{studyData.duration}</span>
                            </div>
                        )}
                        {studyData.location && (
                            <div className="overview-item">
                                <span className="overview-label">Location</span>
                                <span className="overview-value">{studyData.location}</span>
                            </div>
                        )}
                        {studyData.targetEnrollment && (
                            <div className="overview-item">
                                <span className="overview-label">Enrolment</span>
                                <span className="overview-value">
                                    {studyData.currentEnrollment || 0} / {studyData.targetEnrollment} target
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Eligibility Criteria */}
                {studyData.inclusionCriteria?.length > 0 && (
                    <div className="section">
                        <h2 className="section-title">Who Can Participate</h2>
                        <div className="eligibility-grid">
                            {studyData.inclusionCriteria.map((criterion, idx) => (
                                <div key={idx} className="eligibility-card">
                                    <span className="eligibility-icon">{criterion.icon}</span>
                                    <span className="eligibility-label">{criterion.label}</span>
                                    <span className="eligibility-desc">{criterion.description}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Research Team */}
                {studyData.principalInvestigator && (
                    <div className="section">
                        <h2 className="section-title">Research Team</h2>
                        <div className="team-section">
                            <div className="pi-card">
                                <div className="pi-photo">
                                    {studyData.principalInvestigator.photoUrl ? (
                                        <img src={studyData.principalInvestigator.photoUrl} alt={studyData.principalInvestigator.name} />
                                    ) : (
                                        getInitials(studyData.principalInvestigator.name)
                                    )}
                                </div>
                                <div className="pi-info">
                                    <span className="pi-role">Principal Investigator</span>
                                    <span className="pi-name">{studyData.principalInvestigator.name}</span>
                                    <span className="pi-title">{studyData.principalInvestigator.title}</span>
                                    <span className="pi-institution">{studyData.principalInvestigator.institution}</span>
                                </div>
                            </div>
                            {studyData.researchTeam?.length > 0 && (
                                <div className="team-grid">
                                    {studyData.researchTeam.map((member, idx) => (
                                        <div key={idx} className="team-member">
                                            <div className="team-photo">
                                                {member.photoUrl ? (
                                                    <img src={member.photoUrl} alt={member.name} />
                                                ) : (
                                                    getInitials(member.name)
                                                )}
                                            </div>
                                            <div className="team-info">
                                                <span className="team-name">{member.name}</span>
                                                <span className="team-role">{member.role}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Join the Study CTA */}
                <div className="section cta-main-section">
                    <h2 className="section-title">Join the Study</h2>
                    <p className="about-text" style={{textAlign: 'center', marginBottom: '0'}}>
                        Your participation helps advance important research.
                        The enrolment process takes about 10-15 minutes.
                    </p>

                    <div className="cta-section">
                        <button
                            className="btn btn-primary btn-large"
                            onClick={handleOpenEnrollment}
                            disabled={studyData.status !== 'recruiting'}
                        >
                            <span>Check Your Eligibility</span>
                            <span>→</span>
                        </button>
                    </div>
                </div>

                {/* How It Works */}
                <div className="section">
                    <h2 className="section-title">How It Works</h2>
                    <div className="steps-flow">
                        <div className="step-item">
                            <div className="step-number">1</div>
                            <span className="step-title">Check Eligibility</span>
                            <span className="step-desc">Answer a few questions</span>
                        </div>
                        <div className="step-item">
                            <div className="step-number">2</div>
                            <span className="step-title">Give Consent</span>
                            <span className="step-desc">Review & sign e-consent</span>
                        </div>
                        <div className="step-item">
                            <div className="step-number">3</div>
                            <span className="step-title">Get Access Code</span>
                            <span className="step-desc">Receive your unique code</span>
                        </div>
                        <div className="step-item">
                            <div className="step-number">4</div>
                            <span className="step-title">Download App</span>
                            <span className="step-desc">Enter code in SteadyStride</span>
                        </div>
                        <div className="step-item">
                            <div className="step-number">5</div>
                            <span className="step-title">Start Participating</span>
                            <span className="step-desc">Begin your journey</span>
                        </div>
                    </div>
                </div>

                {/* App Download Section */}
                <div className="section app-section">
                    <AppDownloadSection
                        appId={studyData?.mobileApp?.appId || DEFAULT_APP}
                        description="After enrolling, you'll use our app to participate in the study."
                        linkTitle={false}
                    />
                </div>

                {/* FAQs */}
                {studyData.faqs?.length > 0 && (
                    <div className="section">
                        <h2 className="section-title">Frequently Asked Questions</h2>
                        {studyData.faqs.map((faq, idx) => (
                            <div key={idx} className="faq-item">
                                <div className="faq-question">{faq.question}</div>
                                <p className="faq-answer">{faq.answer}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Ethics & Contact */}
                <div className="section">
                    <h2 className="section-title">Ethics & Governance</h2>
                    <div className="ethics-info">
                        {studyData.ethicsApproval?.committee && (
                            <div className="ethics-item">
                                <span className="ethics-label">Ethics Committee</span>
                                <span className="ethics-value">{studyData.ethicsApproval.committee}</span>
                            </div>
                        )}
                        {studyData.ethicsApproval?.referenceNumber && (
                            <div className="ethics-item">
                                <span className="ethics-label">Reference Number</span>
                                <span className="ethics-value">{studyData.ethicsApproval.referenceNumber}</span>
                            </div>
                        )}
                        {studyData.contactEmail && (
                            <div className="ethics-item">
                                <span className="ethics-label">Contact</span>
                                <span className="ethics-value">
                                    <a href={`mailto:${studyData.contactEmail}`}>{studyData.contactEmail}</a>
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Disclaimer */}
                <div className="disclaimer-section">
                    <div className="disclaimer-title">Important Information</div>
                    <p className="disclaimer-text">
                        This is a research study{studyData.sponsor ? ` conducted by ${studyData.sponsor}` : ''}.
                        Participation is voluntary and you may withdraw at any time.
                        {studyData.ethicsApproval?.committee ? ` This study has been approved by the ${studyData.ethicsApproval.committee}.` : ''}
                        {studyData.contactEmail ? ` For questions about the study, please contact ${studyData.contactEmail}.` : ''}
                    </p>
                </div>
            </div>

            {/* Enrollment Modal */}
            <EnrollmentModal
                isOpen={isEnrollmentModalOpen}
                onClose={handleCloseEnrollment}
                studyData={studyData}
            />
        </div>
    );
};

export default StudyPage;
